import { cache } from '@customerio/hydra-nav-state';
import { useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { sentryCaptureException } from '@/utils/sentry';
import { useMountEffect } from '@/hooks/state/use-mount-effect';

/**
 * Subscribes to cross-app cache invalidation events via hydra-nav-state's
 * `cache` namespace and invalidates all active React Query queries.
 *
 * When the copilot or another micro-frontend signals that data has changed
 * (e.g. after a tool execution), this hook debounces and then calls
 * `queryClient.invalidateQueries()` which marks all queries as stale
 * and refetches any that are currently active.
 *
 * Must be rendered inside a `QueryClientProvider`.
 */
export function useCacheInvalidation() {
  const queryClient = useQueryClient();
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInvalidating = useRef(false);
  const pendingInvalidation = useRef(false);

  // queryClient is stable across renders so this is effectively a mount-only effect
  useMountEffect(() => {
    async function invalidateAll() {
      if (isInvalidating.current) {
        pendingInvalidation.current = true;
        return;
      }

      isInvalidating.current = true;

      try {
        await queryClient.invalidateQueries();
      } catch (error) {
        sentryCaptureException(error, {
          extra: {
            context: 'cache-invalidation',
            message: 'Failed to invalidate React Query cache',
          },
        });
      } finally {
        isInvalidating.current = false;

        if (pendingInvalidation.current) {
          pendingInvalidation.current = false;

          if (debounceTimer.current != null) {
            clearTimeout(debounceTimer.current);
          }

          debounceTimer.current = setTimeout(invalidateAll, 500);
        }
      }
    }

    function handleInvalidation() {
      if (debounceTimer.current != null) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(invalidateAll, 500);
    }

    const unsubscribe = cache.onInvalidate(handleInvalidation);

    return () => {
      unsubscribe();

      if (debounceTimer.current != null) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }

      pendingInvalidation.current = false;
    };
  });
}
